import { useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Typography, Box, TextField, Dialog, DialogContent, Checkbox, IconButton, Tooltip, InputAdornment, CircularProgress } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { AgGridReact } from 'ag-grid-react';
import CurrencyInput from 'react-currency-input-field';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';

ProductOrderGuideForm.propTypes = {
  editProduct: PropTypes.object,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function ProductOrderGuideForm({ editProduct, open, close }) {
  const gridRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderGuides, setOrderGuides] = useState([]);

  useEffect(() => {
    if (editProduct && open) {
      setLoading(true);
      axios.get(`/product/productOrderGuideList?id=${editProduct.id}`)
        .then(res => setOrderGuides((res.data ?? []).map((data, index) => ({ ...data, id: index })).sort((prod, nexProd) => nexProd.active - prod.active)))
        .catch(() => enqueueSnackbar('Get product order guide data failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    }
    else setOrderGuides([]);
  }, [open])     

  const onSubmit = () => {
    setSubmitting(true);
    axios.post("/product/productOrderGuideForm", objectToFormData({
      input: orderGuides.map(prod => ({
        price: prod.newPrice ?? prod.price,
        productUOMId: prod.productUOMId,
        orderGuideId: prod.orderGuideId,
        active: prod.active
      })),
    }))
      .then(() => enqueueSnackbar(`Order guide products updated`, { variant: 'success' }))
      .catch(() => enqueueSnackbar(`Update order guide products failed`, { variant: 'error' }))
      .finally(() => {
        setSubmitting(false);
        close();
      });
  };

  const updateRow = (params, field, newValue) => {
    setOrderGuides(orderGuides => {
      const oldOrderGuide = orderGuides.find(guide => guide.id === params.data.id);
      if (!oldOrderGuide) return orderGuides;
      oldOrderGuide[field] = newValue;
      return orderGuides;
    });
    const newNodeData = { ...params.node.data };
    newNodeData[field] = newValue
    params.node.setData(newNodeData);
    if (field === "active") params.api.refreshHeader();
  }

  const updateAllRowActives = (params, active) => {
    const updateRows = [];
    params.api.forEachNodeAfterFilter(node => {
      node.data.active = !active;
      updateRows.push(node.data.id);
    });
    setOrderGuides(orderGuides => {
      orderGuides.filter(guide => updateRows.includes(guide.id)).forEach(guide => { guide.active = !active; });
      return orderGuides;
    });
    params.api.refreshCells();
    params.api.refreshHeader();
  }

  const columns = useMemo(() => [
    {
      field: 'purchasingGroupName',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'facilityName',
      minWidth: 150,
      flex: 1
    },
    {
      field: 'uomType',
      width: 100,
    },
    {
      field: 'price',
      width: 200,
      maxWidth: 200,
      suppressKeyboardEvent: (params) => true,
      valueGetter: (params) => params.data.newPrice ?? params.data.price ?? 0,
      cellRenderer: (params) => <Box>
        {(params.data.newPrice !== null && params.data.newPrice !== undefined && params.data.price !== params.data.newPrice) ?
          <Tooltip title={`Normal price: ${(params.data.price ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} arrow placement="top">
            <IconButton edge="end" color="secondary" onClick={() => updateRow(params, "newPrice", params.data.price ?? 0)} disableRipple sx={{ px: 0 }}>
              <RestoreIcon />
            </IconButton>
          </Tooltip> : ""}
        <CurrencyInput
          name="price"
          size="small"
          placeholder="Please enter a price"
          sx={(params.data.newPrice !== null && params.data.newPrice !== undefined && params.data.price !== params.data.newPrice) ? { ml: 2, width: "calc(100% - 30px)" } : {}}
          customInput={TextField}
          value={params.data.newPrice ?? (params.data.price ?? 0)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          decimalScale={2}
          onValueChange={(value) => {
            if (!value || (value !== params.data.newPrice && !Number.isNaN(Number(value)))) updateRow(params, "newPrice", !value ? 0 : value);
          }}
        />
      </Box>
    },
    {
      field: 'active',
      width: 155,
      cellRenderer: (params) => <Checkbox checked={params.data.active ?? false} onChange={(e) => updateRow(params, "active", e.target.checked)} />,
      headerComponent: (params) => AGGridHeaderCheckboxSimple(params, updateAllRowActives),
    }
  ], []);

  return <Dialog open={open} onClose={close} maxWidth="lg" >
    <DialogContent>
      <Stack spacing={1} minWidth="900px">
        <Typography variant="h3" textAlign="center">{editProduct?.name} - {editProduct?.vendorItemId}</Typography>
        {!loading ? <>
          <AGGridQuickFilterWrapper gridRef={gridRef} />
          <Box>
            <Box sx={{ height: "650px" }}>
              <AgGridReact
                {...AgGridDefaults}
                ref={gridRef}
                columnDefs={[...columns]}
                rowData={orderGuides}
                onFilterChanged={(params) => params.api.refreshHeader()}
              />
            </Box>
          </Box>
        </> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained" onClick={onSubmit}>
            Save
          </LoadingButton>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>;
}
